import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Users, 
  Calendar, 
  PoundSterling, 
  ChevronRight,
  Clock,
  Phone,
  Mail,
  MapPin,
  TrendingUp,
  FileText,
  CheckCircle,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stat Detail Modal
const StatDetailModal = ({ stat, leads, dashboardData, onClose }) => {
  if (!stat) return null;
  
  const getDetailContent = () => {
    switch (stat) {
      case "enquiries":
        return {
          title: "New Enquiries This Month",
          count: dashboardData?.stats?.leads_this_month || 0,
          items: leads.filter(l => l.status === "new_enquiry").slice(0, 5),
          emptyText: "No new enquiries",
          link: "/owner-dashboard/leads",
          linkText: "View All Leads"
        };
      case "quotes":
        return {
          title: "Quotes Booked This Month",
          count: dashboardData?.stats?.quotes_booked || 0,
          items: leads.filter(l => l.status === "quote_booked").slice(0, 5),
          emptyText: "No quotes booked",
          link: "/owner-dashboard/leads",
          linkText: "View All Leads"
        };
      case "jobs":
        return {
          title: "Jobs Won This Month",
          count: dashboardData?.stats?.jobs_won || 0,
          items: leads.filter(l => l.status === "job_won").slice(0, 5),
          emptyText: "No jobs won yet",
          link: "/owner-dashboard/leads",
          linkText: "View All Leads"
        };
      case "revenue":
        return {
          title: "Revenue This Month",
          count: `£${(dashboardData?.stats?.revenue_this_month || 0).toLocaleString()}`,
          items: leads.filter(l => l.status === "job_won" && l.job_value).slice(0, 5),
          emptyText: "No revenue recorded",
          link: "/owner-dashboard/leads",
          linkText: "View All Jobs"
        };
      default:
        return null;
    }
  };
  
  const content = getDetailContent();
  if (!content) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">{content.title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          <p className="text-4xl font-bold text-primary mb-6">{content.count}</p>
          
          {content.items && content.items.length > 0 ? (
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {content.items.map((item, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{item.customer_name || item.name || "Unknown"}</p>
                      <p className="text-sm text-gray-500">{item.project_type || "General"}</p>
                    </div>
                    <p className="text-sm font-medium text-primary">
                      £{item.estimate_low || item.estimated_min || 0} - £{item.estimate_high || item.estimated_max || 0}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">{content.emptyText}</p>
          )}
          
          <Link 
            to={content.link} 
            className="mt-4 block w-full text-center py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            {content.linkText}
            <ChevronRight className="inline w-4 h-4 ml-1" />
          </Link>
        </div>
      </div>
    </div>
  );
};

const DashboardView = ({ owner, dashboardData, leads, onStatClick, expandedStat, setExpandedStat }) => {
  const recentQuotes = dashboardData?.recent_quotes || [];
  const upcomingAppointments = dashboardData?.upcoming_appointments || [];

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric", month: "short"
    });
  };

  return (
    <div className="p-6" data-testid="owner-dashboard-main">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Welcome back, {owner?.name || "Owner"}!</h1>
        <p className="text-gray-600">Here's what's happening with your business</p>
      </div>

      {/* Stats Grid - Clickable Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <button 
          onClick={() => setExpandedStat("enquiries")}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:border-blue-300 hover:shadow-md transition-all text-left group"
          data-testid="stat-enquiries"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{dashboardData?.stats?.leads_this_month || 0}</p>
              <p className="text-sm text-gray-500">New Enquiries</p>
            </div>
          </div>
          <p className="text-xs text-blue-500 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">Click for details →</p>
        </button>

        <button 
          onClick={() => setExpandedStat("quotes")}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:border-purple-300 hover:shadow-md transition-all text-left group"
          data-testid="stat-quotes"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
              <Calendar className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{dashboardData?.stats?.quotes_booked || 0}</p>
              <p className="text-sm text-gray-500">Quotes Booked</p>
            </div>
          </div>
          <p className="text-xs text-purple-500 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">Click for details →</p>
        </button>

        <button 
          onClick={() => setExpandedStat("jobs")}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:border-green-300 hover:shadow-md transition-all text-left group"
          data-testid="stat-jobs"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{dashboardData?.stats?.jobs_won || 0}</p>
              <p className="text-sm text-gray-500">Jobs Won</p>
            </div>
          </div>
          <p className="text-xs text-green-500 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">Click for details →</p>
        </button>

        <button 
          onClick={() => setExpandedStat("revenue")}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:border-amber-300 hover:shadow-md transition-all text-left group"
          data-testid="stat-revenue"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
              <PoundSterling className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">£{(dashboardData?.stats?.revenue_this_month || 0).toLocaleString()}</p>
              <p className="text-sm text-gray-500">Revenue</p>
            </div>
          </div>
          <p className="text-xs text-amber-500 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">Click for details →</p>
        </button>
      </div>

      {/* Recent Activity Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Quote Requests */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Quote Requests</h2>
            <Link to="/owner-dashboard/leads" className="text-sm text-primary hover:text-primary/80">
              View all <ChevronRight className="inline w-4 h-4" />
            </Link>
          </div>
          
          {recentQuotes.length > 0 ? (
            <div className="space-y-3">
              {recentQuotes.slice(0, 5).map((quote, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{quote.customer_name || quote.name}</p>
                      <p className="text-xs text-gray-500">{quote.project_type?.replace(/_/g, " ")}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      £{quote.estimated_min || quote.estimate_low || 0} - £{quote.estimated_max || quote.estimate_high || 0}
                    </p>
                    <p className="text-xs text-gray-400">{formatDate(quote.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No recent quote requests</p>
            </div>
          )}
        </div>

        {/* Upcoming Appointments */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Upcoming Appointments</h2>
            <Link to="/owner-dashboard/calendar" className="text-sm text-primary hover:text-primary/80">
              Manage <ChevronRight className="inline w-4 h-4" />
            </Link>
          </div>
          
          {upcomingAppointments.length > 0 ? (
            <div className="space-y-3">
              {upcomingAppointments.slice(0, 5).map((apt, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{apt.customer_name || apt.name}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        {apt.booking_time || "TBD"}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{formatDate(apt.booking_date)}</p>
                    <p className="text-xs text-gray-400">{apt.postcode}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No upcoming appointments</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/owner-dashboard/pipeline" className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white hover:from-blue-600 hover:to-blue-700 transition-all">
          <TrendingUp className="w-6 h-6 mb-2" />
          <p className="font-medium">Sales Pipeline</p>
          <p className="text-xs text-blue-100">Manage your CRM</p>
        </Link>
        <Link to="/owner-dashboard/operations" className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white hover:from-purple-600 hover:to-purple-700 transition-all">
          <CheckCircle className="w-6 h-6 mb-2" />
          <p className="font-medium">Project Ops</p>
          <p className="text-xs text-purple-100">Track active jobs</p>
        </Link>
        <Link to="/owner-dashboard/payments" className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white hover:from-green-600 hover:to-green-700 transition-all">
          <PoundSterling className="w-6 h-6 mb-2" />
          <p className="font-medium">Payments</p>
          <p className="text-xs text-green-100">Manage invoices</p>
        </Link>
        <Link to="/owner-dashboard/analytics" className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white hover:from-amber-600 hover:to-amber-700 transition-all">
          <TrendingUp className="w-6 h-6 mb-2" />
          <p className="font-medium">Analytics</p>
          <p className="text-xs text-amber-100">View reports</p>
        </Link>
      </div>

      {/* Stat Detail Modal */}
      <StatDetailModal 
        stat={expandedStat} 
        leads={leads}
        dashboardData={dashboardData}
        onClose={() => setExpandedStat(null)} 
      />
    </div>
  );
};

export default DashboardView;
